import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import ShareWhatsApp from "@/components/ShareWhatsApp";

export default function Notices() {
    const { user } = useAuth();
    const [notices, setNotices] = useState([]);
    const [form, setForm] = useState({ title: "", body: "", category: "general" });
    const isAdmin = user?.role === "admin";

    const load = async () => {
        try { const r = await api.get("/notices"); setNotices(r.data); } catch {}
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const r = await api.post("/notices", form);
            setNotices((arr) => [r.data, ...arr]);
            setForm({ title: "", body: "", category: "general" });
            toast.success("Notice posted");
        } catch { toast.error("Failed"); }
    };

    const remove = async (id) => {
        try { await api.delete(`/notices/${id}`); setNotices((arr) => arr.filter((n) => n.notice_id !== id)); toast.success("Removed"); } catch { toast.error("Failed"); }
    };

    return (
        <div className="animate-fade-up" data-testid="notices-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 sm:gap-4">
                <div>
                    <p className="nest-stat-label">Notices</p>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1 sm:mt-2">From the noticeboard</h1>
                    <p className="text-nest-ink2 text-sm sm:text-base mt-1 sm:mt-2">Announcements, alerts and reminders for everyone in the nest.</p>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 mt-6 sm:mt-10">
                {isAdmin && (
                    <form onSubmit={submit} className="nest-card p-6 lg:sticky lg:top-24 self-start" data-testid="notice-form">
                        <p className="nest-stat-label">Compose</p>
                        <h3 className="font-display text-xl mt-1 tracking-tight">Publish a notice</h3>
                        <div className="mt-5 grid gap-4">
                            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="notice-title-input"/>
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="notice-category-input">
                                <option value="general">General</option>
                                <option value="urgent">Urgent</option>
                                <option value="event">Event</option>
                                <option value="maintenance">Maintenance</option>
                            </select>
                            <textarea required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What's the announcement?" className="bg-transparent border border-nest-line rounded-md focus:border-nest-clay outline-none p-3" data-testid="notice-body-input"/>
                            <button type="submit" className="nest-btn-primary" data-testid="notice-submit-btn">Publish</button>
                        </div>
                    </form>
                )}
                <div className={`${isAdmin ? "lg:col-span-2" : "lg:col-span-3"} space-y-4`}>
                    {notices.length === 0 && <div className="nest-card p-10 text-center text-nest-ink2" data-testid="no-notices">No notices yet.</div>}
                    {notices.map((n) => (
                        <article key={n.notice_id} className="nest-card p-6" data-testid={`notice-${n.notice_id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-nest-clay">{n.category}</span>
                                    <h3 className="font-display text-2xl tracking-tight mt-1">{n.title}</h3>
                                </div>
                                {isAdmin && (
                                    <button onClick={() => remove(n.notice_id)} className="text-nest-ink3 hover:text-nest-err" data-testid={`notice-delete-${n.notice_id}`}><Trash2 size={16}/></button>
                                )}
                            </div>
                            <p className="text-nest-ink2 mt-3 whitespace-pre-wrap leading-relaxed">{n.body || n.description}</p>
                            {n.photo_url && (
                                <div className="mt-3 rounded-2xl overflow-hidden border border-nest-line max-w-md bg-nest-alt shadow-xs">
                                    <img
                                        src={n.photo_url}
                                        alt="Notice attachment"
                                        className="w-full h-auto max-h-80 object-cover"
                                    />
                                </div>
                            )}
                            <div className="nest-divider my-4"></div>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="text-xs text-nest-ink3">{n.author_name || n.author || "Security Desk"} · {new Date(n.created_at).toLocaleString()}</p>
                                <ShareWhatsApp
                                    testid={`notice-share-${n.notice_id}`}
                                    label="Share to group"
                                    text={`📣 *${n.title}* (${n.category})\n\n${n.body || n.description}\n\n— ${n.author_name || n.author || "NeighborNest"}`}
                                />
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
}
